import { useTranslation } from 'react-i18next';

interface Props {
  label?: string;
}

/**
 * Full-screen loading state shown while the WebApp authenticates.
 *
 * Composition: the cartoon whale mascot bobs in place above a
 * dotted waterline; a pair of bubbles rises from below at staggered
 * timings; three sparkle dots twinkle around the mascot. All motion
 * is pure CSS so the loader keeps rendering even before any JS
 * hydration finishes. Works on light + dark themes — the mascot is
 * a self-coloured sticker, the waterline + sparkles use brand-accent
 * CSS vars that flip with the theme.
 */
export function WhaleLoader({ label }: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6">
      <div className="kit-loader-scene" aria-hidden>
        {/* Sparkle dots floating around the mascot — staggered so
            they twinkle in/out at different beats. */}
        <span className="kit-sparkle kit-sparkle-a" />
        <span className="kit-sparkle kit-sparkle-b" />
        <span className="kit-sparkle kit-sparkle-c" />

        {/* The mascot — gentle in-place bob, no horizontal drift so
            the sparkles + bubbles stay anchored. */}
        <img
          src="/whale-mascot.webp"
          alt=""
          width={512}
          height={341}
          className="kit-loader-mascot kit-loader-bob"
        />

        {/* Rising bubbles — two trails at different x positions and
            durations so the pattern doesn't look mechanical. */}
        <span className="kit-loader-bubble kit-loader-bubble-a" />
        <span className="kit-loader-bubble kit-loader-bubble-b" />

        {/* Dotted waterline under the mascot. */}
        <span className="kit-loader-waterline" />
      </div>
      <span className="pb-caption tracking-wide text-tg-hint">
        {label ?? t('auth.signing_in')}
      </span>
    </div>
  );
}
